import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { skip, take } from 'rxjs';
import { isEmpty } from 'lodash-es';

import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { SUBSCRIPTION_TYPES } from 'src/app/models/payment';
import {
  RecordingMode,
  User,
  InstitutionPermissionState,
  EventEndCountdownTimerVisibility,
  DEFAULT_SHARE_SNIPPETS_TRIGGER,
} from 'src/app/models/user';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { UserSettingsPanelData } from 'src/app/services/settings.service';
import {
  ConfirmationModalComponent,
  ModalStandardTemplateEnum,
} from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { ColorScheme } from 'src/app/standalones/components/pencil-bottom-sheet-container/pencil-bottom-sheet-container.component';
import { PaymentService } from 'src/app/services/payment.service';
import { LayoutSize, ScreenSizeService } from 'src/app/services/screen-size.service';
import { NAME_VALIDATORS, cssColorValidator } from 'src/app/common/utils/form-validators';
import { CapabilityTierService } from 'src/app/services/capability-tier.service';
import { AccessControl } from '@pncl/common-models';
import { RecordingAccess } from 'src/app/models/space-recording';
import { Visibility } from 'src/app/models/session';
import { isNativeApp } from 'src/app/utilities/native-app.utils';
import { PanelData, SettingsConstantsTransformer, panelID } from '../settings.constants';
import { UserService } from '../../services/user.service';
import { AclService, Feature } from '../../services/acl.service';
import { FeatureManagementService } from '../../services/feature-management.service';
import { Themes } from '../../services/theme.service';
import { IconTypes } from './../../standalones/components/pencil-icon/pencil-icon.component';
import Capability = AccessControl.Capability;

@UntilDestroy()
@Component({
  selector: 'app-user-settings-panel',
  templateUrl: './user-settings-panel.component.html',
  styleUrls: ['./user-settings-panel.component.scss'],
})
export class UserSettingsPanelComponent implements OnInit, OnDestroy {
  activePanelData?: PanelData;
  PANELS;
  accountForm?: UntypedFormGroup;
  institutionForm?: UntypedFormGroup;
  advancedSettingsForm?: UntypedFormGroup;
  aiAssistForm?: UntypedFormGroup;
  feedbackForm?: UntypedFormGroup;
  eventsForm?: UntypedFormGroup;
  brandingForm?: UntypedFormGroup;
  sessionsForm?: UntypedFormGroup;
  isRTL = false;
  isNonDesktop = false;
  BillingStatus = SUBSCRIPTION_TYPES;
  currentBilling = SUBSCRIPTION_TYPES.TRIAL;
  isBillingEnabled = false;
  user?: User;
  domListener = this.domListenerFactoryService.createInstance();
  showInstitutionSettings = false;
  isInstitutionUser = false;
  inactivityNotificationsEnabled = false;
  // TODO: Migrate to SettingsManagerService.disabledTabs$ (use FLAGS.SETTINGS_TABS_VISIBILITY variables)
  isBillableUsageSettingEnabled = this.flagsService.isFlagEnabled(
    FLAGS.ENABLE_INSTITUTION_BILLING_SETTINGS,
  );
  isNewSessionsFlowEnabled = this.flagsService.isFlagEnabled(FLAGS.ENABLE_NEW_SESSIONS_FLOW);
  isSchedulingEnabled = false;
  isSitesEnabled = false;
  isBrandingEnabled = false;
  formChanged: boolean = false;
  hasBillingStrategyWithHiddenBillingUi = false;
  isCustomAttributesEnabled = false;
  isAlertsEnabled = false;
  isNativeApp = isNativeApp();
  readonly ColorScheme = ColorScheme;

  constructor(
    private translateService: TranslateService,
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    public userService: UserService,
    private flagsService: FlagsService,
    private screenSizeService: ScreenSizeService,
    private domListenerFactoryService: DomListenerFactoryService,
    private aclService: AclService,
    private paymentService: PaymentService,
    public featureManagementService: FeatureManagementService,
    private capabilityTierService: CapabilityTierService,
    private dialogRef?: MatDialogRef<UserSettingsPanelComponent>,
    private bottomSheetRef?: MatBottomSheetRef<UserSettingsPanelComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData?: UserSettingsPanelData,
    @Inject(MAT_BOTTOM_SHEET_DATA) private bottomSheetData?: UserSettingsPanelData,
  ) {
    const panelData = isEmpty(this.bottomSheetData) ? this.dialogData : this.bottomSheetData;
    this.setupKeyListenerForClosingModal();
    // TODO: Migrate to SettingsManagerService.disabledTabs$ (use FLAGS.SETTINGS_TABS_VISIBILITY variables)
    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_BILLING)) {
      this.isBillingEnabled = true;
    }
    const settingsConstants = new SettingsConstantsTransformer(this.translateService);
    this.PANELS = settingsConstants.PANELS;
    this.isNonDesktop = this.screenSizeService.layoutSize !== LayoutSize.Desktop;
    this.screenSizeService.layoutSize$
      .pipe(
        untilDestroyed(this),
        // Skip the synchronous initial emission - isNonDesktop is seeded above, and
        // reacting to it here would dismiss the panel as it opens.
        skip(1),
      )
      .subscribe((layoutSize: LayoutSize) => {
        this.isNonDesktop = layoutSize !== LayoutSize.Desktop;
        this.bottomSheetRef?.dismiss?.();
        this.dialogRef?.close?.();
      });
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;

      if (res?.user?.subscriptionType) {
        this.currentBilling = res?.user?.subscriptionType;
      }
      this.showInstitutionSettings =
        !!res?.user && this.aclService.isAllowed(res.user, Feature.edit_institution);
      this.isInstitutionUser = Boolean(res?.user.institution);
      this.hasBillingStrategyWithHiddenBillingUi =
        this.paymentService.hasBillingStrategyWithHiddenBillingUi(this.user?.institution);
    });

    // Initialize all forms here and pass them to subpanels
    this.accountForm = this.formBuilder.group({
      name: this.formBuilder.control('', {
        validators: NAME_VALIDATORS,
      }),
      profile_image: [''],
      phone: this.formBuilder.control('', {
        updateOn: 'change',
      }),
    });

    this.advancedSettingsForm = this.formBuilder.group(
      {
        settings: this.formBuilder.group({
          inactivityNotifications: [false, Validators.required],
        }),
      },
      { updateOn: 'change' },
    );

    this.institutionForm = this.formBuilder.group(
      {
        // institution settings
        settings: this.formBuilder.group({
          addAdminsAsHostsInSpace: [false, Validators.required],
          allowAdminsAsObserversInSpace: [false, Validators.required],
          autoDesignateTeachersAsHosts: [false, Validators.required],
          preventTeachersfromCreatingSpaces: [false, Validators.required],
          preventStudentsFromEditingDetails: [false, Validators.required],
          inactivityNotifications: [true, Validators.required],
          crRecordingMode: [RecordingMode.MANUAL, Validators.required],
          crEnableHostDeletion: [false, Validators.required],
          crEnableDownloads: [false, Validators.required],
          crEnablePublicRecordings: [false, Validators.required],
          crForceAdminOnlyAccess: [false, Validators.required],
          crDefaultAccess: [RecordingAccess.HOSTS, Validators.required],
          crEnableAISummarization: [false, Validators.required],
          crEnablePrivacyAwareCloudRecording: [false, Validators.required],
          forceScreenShareInAutomaticCr: [false, Validators.required],
          preventHostsFromRecordingLocally: [false, Validators.required],
          liveCallAlerts: [false, Validators.required],
          hideSpaceBackLinks: [false, Validators.required],
          preventEventEmails: [false, Validators.required],
          enableNudityDetectionAlerts: [false, Validators.required],
          preventMakingSpacesPublic: [false, Validators.required],
          preventAddingExternalUsersToSpaces: [false, Validators.required],
          defaultSpaceVisibility: [Visibility.PUBLIC, Validators.required],
        }),
        // inside spaces permissions for institution
        permissions: this.formBuilder.group({
          enableWaitingRoom: [InstitutionPermissionState.DEFAULT_OFF, Validators.required],
          messageOtherParticipants: [InstitutionPermissionState.DEFAULT_OFF, Validators.required],
          leaderMode: [InstitutionPermissionState.DEFAULT_OFF, Validators.required],
          downloadSpacesOrBoards: [InstitutionPermissionState.ALWAYS_OFF, Validators.required],
          childMode: [InstitutionPermissionState.DEFAULT_OFF, Validators.required],
        }),
        // LTI settings
        ltiSettings: this.formBuilder.array([]),
      },
      { updateOn: 'change' },
    );

    this.aiAssistForm = this.formBuilder.group(
      {
        settings: this.formBuilder.group({
          inactivityNotifications: [false, Validators.required],
          crEnableAISummarization: [false, Validators.required],
          liveCallAlerts: [false, Validators.required],
          aiCoachingInsights: [false, Validators.required],
          aiSafeguarding: [false, Validators.required],
          aiSnippets: [false, Validators.required],
          shareSnippetsSettings: this.formBuilder.group({
            trigger: [DEFAULT_SHARE_SNIPPETS_TRIGGER, Validators.required],
          }),
          enableNudityDetectionAlerts: [false, Validators.required],
          summaryEmailAudience: [[]],
          coachingEmailAudience: [[]],
          snippetsEmailAudience: [[]],
        }),
      },
      { updateOn: 'change' },
    );

    this.feedbackForm = this.formBuilder.group(
      {
        settings: this.formBuilder.group({
          enablePostEventAction: [false, Validators.required],
          eventEndCountdownTimerVisibility: [
            EventEndCountdownTimerVisibility.ALWAYS_SHOW,
            Validators.required,
          ],
          enableExtendEvent: [true, Validators.required],
        }),
      },
      { updateOn: 'change' },
    );

    this.eventsForm = this.formBuilder.group(
      {
        settings: this.formBuilder.group({
          enableAutoStartCall: [true, Validators.required],
          attendanceThresholds: [[]],
        }),
      },
      { updateOn: 'change' },
    );

    this.sessionsForm = this.formBuilder.group(
      {
        settings: this.formBuilder.group({
          crEnablePublicRecordings: [false, Validators.required],
        }),
      },
      { updateOn: 'change' },
    );

    this.brandingForm = this.formBuilder.group(
      {
        settings: this.formBuilder.group({
          horizontalLogo: [''],
          waterMarkLogo: [''],
          squareLogo: [''],
          sidebarBG: [Themes.default.colors['--sidebar-background'], cssColorValidator()],
          switcherBG: [Themes.default.colors['--switcher-background'], cssColorValidator()],
          sidebarActiveBG: [
            Themes.default.colors['--sidebar-active-background'],
            cssColorValidator(),
          ],
          topBarBackground: [Themes.default.colors['--top-bar-background'], cssColorValidator()],
          galleryBackground: [
            Themes.default.colors['--gallery-background'],
            cssColorValidator(true),
          ], // Validator supports gradients
          greyContainerBackground: [
            Themes.default.colors['--grey-container-background'],
            cssColorValidator(),
          ],
          galleryBottomBarBackground: [
            Themes.default.colors['--gallery-bottom-bar-background'],
            cssColorValidator(),
          ],
          primaryBrandColor: [Themes.default.colors['--primary-brand-color'], cssColorValidator()],
          virtualBackgroundUrls: [[]],
          onlyAllowInstitutionVirtualBackgrounds: [false],
        }),
      },
      { updateOn: 'change' },
    );

    if (!this.isNonDesktop) {
      this.activePanelData = this.PANELS.ACCOUNT;
    }

    if (panelData?.openTab) {
      this.switchSubPanel(panelData.openTab);
    }

    // TODO: Migrate to SettingsManagerService.disabledTabs$ (use FLAGS.SETTINGS_TABS_VISIBILITY variables)
    this.flagsService
      .featureFlagChanged(FLAGS.INACTIVITY_NOTIFICATIONS)
      .pipe(untilDestroyed(this))
      .subscribe(
        (inactivityNotificationsEnabled) =>
          (this.inactivityNotificationsEnabled = inactivityNotificationsEnabled),
      );

    this.isSchedulingEnabled = this.featureManagementService.isSchedulingEnabled();
    this.isSitesEnabled = this.capabilityTierService.shouldEnableCapability(Capability.SITES);
    this.isBrandingEnabled = this.capabilityTierService.shouldEnableCapability(Capability.BRANDING);
    this.isCustomAttributesEnabled = this.featureManagementService.isCustomAttributesEnabled();
    this.isAlertsEnabled = this.capabilityTierService.shouldEnableCapability(
      Capability.CONTROL_CENTER_ALERTS,
    );
  }
  ngOnDestroy(): void {
    this.domListener.clear();
  }

  private setupKeyListenerForClosingModal() {
    this.domListener.add(
      'document',
      'keyup.esc',
      () => {
        this.closePanel();
      },
      true,
    );
  }

  ngOnInit(): void {
    this.userService.rtl.pipe(untilDestroyed(this)).subscribe((res) => (this.isRTL = res));
  }

  async closePanel(): Promise<void> {
    if (this.formChanged) {
      const confirm = await this.confirmUnsavedChanges();
      if (!confirm) {
        return;
      }
    }

    if (this.isNonDesktop) {
      this.bottomSheetRef?.dismiss();
    } else {
      this.dialogRef?.close();
    }
  }

  async switchSubPanel(id?: panelID): Promise<void> {
    if (
      (id === panelID.payment || id === panelID.billableUsage) &&
      isNativeApp() &&
      !window.location.search.includes('openExternal=true')
    ) {
      window.location.href = '/settings?tab=payment&openExternal=true';
      return;
    }

    if (this.formChanged) {
      const confirm = await this.confirmUnsavedChanges();
      if (!confirm) {
        return;
      }
    }

    this.activePanelData = this.findPanel(id);
    this.formChanged = false;
  }

  private findPanel(id?: panelID): PanelData | undefined {
    if (!id) {
      return undefined;
    }
    return (Object.values(this.PANELS) as PanelData[]).find((p) => p.id === id);
  }

  handleFormChanged(changedValue: boolean) {
    this.formChanged = changedValue;
  }

  async confirmUnsavedChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      this.dialog
        .open(ConfirmationModalComponent, {
          data: {
            type: ModalStandardTemplateEnum.UNSAVED_CHANGES,
          },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((result) => {
          resolve(result === 'confirm');
        });
    });
  }

  protected IconTypes = IconTypes;
}
